import { DestroyRef, Injectable, NgZone, inject, signal } from '@angular/core';
import { LOADING_TIMINGS } from '@shared/constants/loading-timings.constants';

/**
 * Global loading indicator service
 * Tracks pending HTTP requests and provides a reactive loading state
 */
@Injectable({
  providedIn: 'root',
})
export class LoadingIndicatorService {
  private readonly ngZone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);
  private pendingHttpRequests = signal(0);
  private pendingRouteNavigations = signal(0);
  private startDelayTimeout: ReturnType<typeof setTimeout> | null = null;
  private completeStartTimeout: ReturnType<typeof setTimeout> | null = null;
  private completeTimeout: ReturnType<typeof setTimeout> | null = null;
  private visibleSince: number | null = null;
  
  /**
   * True when one or more HTTP requests are pending
   */
  readonly isLoading = signal(false);
  
  /**
   * Progress percentage for linear loading bar (0-100)
   * Simulates progress even for requests without known duration
   */
  readonly progress = signal(0);
  
  private progressInterval: ReturnType<typeof setInterval> | null = null;

  constructor() {
    this.destroyRef.onDestroy(() => this.reset());
  }

  private totalPending(): number {
    return this.pendingHttpRequests() + this.pendingRouteNavigations();
  }

  /**
   * Increment pending request count and start progress simulation
   */
  startLoading(kind: 'http' | 'route' = 'http'): void {
    const current = this.totalPending();

    if (kind === 'route') {
      this.pendingRouteNavigations.set(this.pendingRouteNavigations() + 1);
    } else {
      this.pendingHttpRequests.set(this.pendingHttpRequests() + 1);
    }

    this.clearCompleteTimeout();
    this.clearCompleteStartTimeout();
    
    if (current === 0) {
      this.clearStartDelay();
      // Defer showing the indicator to avoid flashes on very fast responses
      this.startDelayTimeout = setTimeout(() => {
        this.startDelayTimeout = null;
        if (this.totalPending() === 0) {
          return;
        }
        // Use queueMicrotask to avoid ExpressionChangedAfterItHasBeenCheckedError
        queueMicrotask(() => {
          this.isLoading.set(true);
          this.visibleSince = Date.now();
          this.progress.set(LOADING_TIMINGS.globalBar.initialPercent);
          this.startProgressSimulation();
        });
      }, LOADING_TIMINGS.globalBar.showDelayMs);
    }
  }

  /**
   * Decrement pending request count and complete progress when done
   */
  stopLoading(kind: 'http' | 'route' = 'http'): void {
    if (kind === 'route') {
      const currentRoute = this.pendingRouteNavigations();
      this.pendingRouteNavigations.set(Math.max(0, currentRoute - 1));
    } else {
      const currentHttp = this.pendingHttpRequests();
      this.pendingHttpRequests.set(Math.max(0, currentHttp - 1));
    }

    const next = this.totalPending();
    
    if (next === 0) {
      // If loading never became visible, cancel pending start and reset quietly
      if (!this.isLoading()) {
        this.clearStartDelay();
        this.resetSignals();
        return;
      }
      this.scheduleCompleteProgress();
    }
  }

  /**
   * Force a reset (used for hard failures)
   */
  reset(): void {
    this.clearStartDelay();
    this.clearCompleteStartTimeout();
    this.clearCompleteTimeout();
    this.resetSignals();
    this.clearProgressInterval();
  }

  private scheduleCompleteProgress(): void {
    this.clearCompleteStartTimeout();
    this.completeStartTimeout = setTimeout(() => {
      this.completeStartTimeout = null;
      if (this.totalPending() > 0 || !this.isLoading()) {
        return;
      }
      const shownFor = this.visibleSince ? Date.now() - this.visibleSince : 0;
      const remainingVisible = LOADING_TIMINGS.globalBar.minVisibleMs - shownFor;
      if (remainingVisible > 0) {
        this.completeStartTimeout = setTimeout(() => {
          this.completeStartTimeout = null;
          if (this.totalPending() === 0 && this.isLoading()) {
            this.completeProgress();
          }
        }, remainingVisible);
        return;
      }
      this.completeProgress();
    }, LOADING_TIMINGS.globalBar.idleGraceMs);
  }

  /**
   * Simulate progress for better UX (can't know actual progress)
   * Asymptotically approaches 90%, never quite reaching 100%
   */
  private startProgressSimulation(): void {
    this.clearProgressInterval();

    this.progressInterval = setInterval(() => {
      const current = this.progress();
      if (current < 85) {
        // Slow down as we approach 85% for a less prominent bar
        const cap = LOADING_TIMINGS.globalBar.asymptoticCapPercent;
        const increment = (cap - current) * 0.08;
        this.progress.set(Math.min(cap, current + increment));
      }
    }, LOADING_TIMINGS.globalBar.progressTickMs);
  }

  /**
   * Complete the progress animation and reset
   */
  private completeProgress(): void {
    this.clearProgressInterval();

    // Quick animation to 100%
    this.progress.set(100);

    // Reset after animation completes. Track the handle so parallel
    // start/stop cycles don't leave orphaned timeouts hanging.
    this.clearCompleteTimeout();
    this.completeTimeout = setTimeout(() => {
      this.completeTimeout = null;
      // Use queueMicrotask to avoid ExpressionChangedAfterItHasBeenCheckedError
      queueMicrotask(() => {
        this.isLoading.set(false);
        this.progress.set(0);
        this.visibleSince = null;
      });
    }, LOADING_TIMINGS.globalBar.settleMs);
  }

  private clearStartDelay(): void {
    if (this.startDelayTimeout) {
      clearTimeout(this.startDelayTimeout);
      this.startDelayTimeout = null;
    }
  }

  private clearCompleteTimeout(): void {
    if (this.completeTimeout) {
      clearTimeout(this.completeTimeout);
      this.completeTimeout = null;
    }
  }

  private clearCompleteStartTimeout(): void {
    if (this.completeStartTimeout) {
      clearTimeout(this.completeStartTimeout);
      this.completeStartTimeout = null;
    }
  }

  private clearProgressInterval(): void {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }
  }

  private resetSignals(): void {
    this.isLoading.set(false);
    this.progress.set(0);
    this.visibleSince = null;
    this.pendingHttpRequests.set(0);
    this.pendingRouteNavigations.set(0);
  }
}
